import { Button, Link, Stack, Typography } from '@mui/material';
import type { LinkProps } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import { CopyButton } from '@components/CopyButton';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import Icon from '@components/Icon/Icon';
import IconButton from '@components/IconButton';
import { Tag } from '@components/Tag';

import { PATIENTS } from '@constants/routes';

import { useRevokeSharedLinkAccess } from '@hooks/api/shared_resource';
import { useCuration } from '@hooks/useCuration';
import { useIsMobile } from '@hooks/useIsMobile';
import { useNavigate } from '@hooks/useNavigate';
import { useSession } from '@hooks/useSession';

import { formatDate, formatName } from '@utils/format';

import type { ListSharedResourceHistoryQuery } from 'generated/shared_resource.generated';
import type {
  SharedResourceHistory,
  SharedResourceModel,
  StatusModel,
  UserModel,
} from 'generated/types';

import SharingMobileRow from './MobileShareHistoryRow';

const messages = defineMessages({
  noData: {
    id: 'shareHistory.noHistory',
    defaultMessage: 'No Share History',
  },
  revokeAccess: {
    id: 'shareHistory.revokeAccess',
    defaultMessage: 'Revoke Access',
  },
  copyLink: {
    id: 'shareHistory.copyLink',
    defaultMessage: 'Copy Link',
  },
  recreateLink: {
    id: 'shareHistory.recreateLink',
    defaultMessage: 'Re-create Link',
  },
  confirm: {
    id: 'shareHistory.revokeAccessConfirm',
    defaultMessage: 'Are you sure you want to revoke access for {email}?',
  },
});

type RowData = {
  created_at: string;
  recipient_email: string;
  expires?: string | null;
  patient_user?: Pick<UserModel, 'first_name' | 'last_name' | 'user_id'> | null;
  resource: {
    resource: SharedResourceModel;
    patient_user_id?: string;
  };
  status: Pick<StatusModel, 'color' | 'public_name' | 'status_id'>;
  shared_resource: SharedResourceHistory;
};

interface ShareHistoryTableProps {
  data: ListSharedResourceHistoryQuery['listSharedResourceHistory'];
}

const ShareHistoryTable = ({ data }: ShareHistoryTableProps) => {
  const { formatMessage } = useIntl();
  const {
    state: { isPractitioner },
  } = useSession();
  const { viewAsPatient } = useCuration();
  const { navigateRoute } = useNavigate();

  const [revokeAccess, revokeAccessLoading] = useRevokeSharedLinkAccess();

  const isMobile = useIsMobile('md');

  const tableData = data.map((shared_resource) => {
    const {
      created_at,
      recipient_email,
      expires,
      patient_user,
      resource,
      status,
    } = shared_resource;

    return {
      created_at,
      recipient_email,
      expires,
      patient_user,
      resource: {
        resource,
        patient_user_id: patient_user?.user_id,
      },
      shared_resource,
      status,
    };
  });

  const removeLinkAccess = async (email: string, share_id: string) => {
    await revokeAccess({
      input: {
        recipient: email.toLowerCase(),
        share_id,
      },
    });
  };

  const linkSxProps: LinkProps = {
    sx: {
      textDecoration: 'none',
    },
  };

  return (
    <MuiDataTable
      columns={[
        {
          name: 'created_at',
          className: 'date',
          label: 'Created On',
          options: {
            customBodyRender: (value: string) =>
              formatDate({ date: value, formatter: 'datetime' }),
          },
        },
        {
          name: 'patient_user',
          className: 'blue-hover',
          label: 'Patient',
          options: {
            display: isPractitioner,
            customBodyRender: (user: RowData['patient_user']) => {
              if (!user) {
                return null;
              }
              const { first_name, last_name, user_id } = user;
              return (
                <Link
                  {...linkSxProps}
                  onClick={() => {
                    viewAsPatient(user_id);
                    navigateRoute(`${PATIENTS}${user_id}`);
                  }}
                >
                  {formatName(first_name, last_name)}
                </Link>
              );
            },
          },
        },
        {
          name: 'recipient_email',
          label: 'Recipient',
          options: {
            customBodyRender: (email: string) => (
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: 'inherit',
                }}
                title={email}
              >
                {email}
              </Typography>
            ),
            setCellProps: () => ({
              style: {
                maxWidth: isMobile ? undefined : '250px',
              },
            }),
          },
        },
        {
          name: 'resource',
          label: 'Shared Record',
          className: 'blue-hover',
          options: {
            customBodyRender: (data: RowData['resource']) => {
              const { name, route } = data.resource;
              return (
                <Link
                  {...linkSxProps}
                  onClick={() => {
                    if (isPractitioner && data.patient_user_id) {
                      viewAsPatient(data.patient_user_id);
                    }
                    navigateRoute(route);
                  }}
                >
                  {name}
                </Link>
              );
            },
          },
        },
        {
          name: 'status',
          label: 'Status',
          options: {
            customBodyRender: (
              status: Pick<StatusModel, 'color' | 'public_name' | 'status_id'>
            ) => {
              const { color = null, public_name } = status;
              return (
                <Tag color={color} label={public_name} variant="secondary" />
              );
            },
          },
        },
        {
          name: 'expires',
          label: 'Expires On',
          className: 'date',
          options: {
            customBodyRender: (value: string) =>
              formatDate({ date: value, formatter: 'datetime' }),
          },
        },
        {
          name: 'shared_resource',
          label: 'Actions',
          options: {
            customBodyRender: ({
              expires,
              recipient_email,
              share_id,
              share_link,
              status: { public_name },
            }: SharedResourceHistory) => {
              // Do not show 'Revoke Access' btn if share link
              // or revoked or expired
              if (
                public_name === 'Revoked' ||
                public_name === 'Expired' ||
                dayjs().isAfter(dayjs(expires))
              ) {
                return '';
              }
              return (
                <Stack
                  alignItems={isMobile ? 'flex-start' : 'flex-end'}
                  justifyContent={isMobile ? 'flex-end' : 'flex-start'}
                  direction={isMobile ? 'row' : 'column'}
                  spacing={isMobile ? 1 : 0}
                >
                  <CopyButton
                    buttonProps={{
                      color: 'info',
                      startIcon: <Icon name="copy" />,
                      sx: {
                        textTransform: 'capitalize',
                      },
                    }}
                    buttonText={
                      isMobile ? undefined : formatMessage(messages.copyLink)
                    }
                    content={share_link}
                    iconButtonProps={{
                      name: 'copy',
                      iconType: 'fad',
                      color: 'info.main',
                      size: 18,
                    }}
                    variant={isMobile ? 'icon' : 'button'}
                  />

                  <ConfirmationDialog
                    onConfirm={() =>
                      removeLinkAccess(recipient_email, share_id)
                    }
                    title={formatMessage(messages.confirm, {
                      email: recipient_email,
                    })}
                  >
                    {({ handleOpen }) => {
                      return isMobile ? (
                        <IconButton
                          color="error.main"
                          disabled={revokeAccessLoading}
                          onClick={handleOpen}
                          iconType="fad"
                          name="ban"
                        />
                      ) : (
                        <Button
                          color="error"
                          disabled={revokeAccessLoading}
                          onClick={handleOpen}
                          startIcon={<Icon name="ban" />}
                          sx={{ textTransform: 'capitalize' }}
                          variant="text"
                        >
                          {formatMessage(messages.revokeAccess)}
                        </Button>
                      );
                    }}
                  </ConfirmationDialog>
                </Stack>
              );
            },
          },
        },
      ]}
      data={tableData}
      options={{
        ...(isMobile
          ? {
              customRowRender: (data: React.ReactNode[], dataIndex: number) => (
                <SharingMobileRow data={data} dataIndex={dataIndex} />
              ),
            }
          : { customRowRender: undefined }),
      }}
    />
  );
};

export default ShareHistoryTable;
